import classNames from 'classnames';
import { differenceInDays, differenceInHours } from 'date-fns';
import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useSelector } from 'react-redux';

import { apiGetTrialStatus } from 'src/api/organizationsubscription';
import { HeaderBannerOnShowType } from 'src/components/Header/index';
import { InfoIcon } from 'src/components/Icons/Info';
import { UiButton } from 'src/components/UI/Button';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { FULL_DATE_FORMAT } from 'src/config';
import { commonSelector, deCommonCurrentPartnerSelector } from 'src/store/commonPart/selector';
import { showPlusSubscriptionModal } from 'src/store/plusSubscription/helpers';
import { currentUserSelector } from 'src/store/user/selectors';
import { CDate } from 'src/utils/CDate';
import { useTHelper } from 'src/utils/i18n';
import { eventPublishShowPlusSubscriptionTrialBanner } from 'src/utils/outer';

type Props = { date: Date };

export const HeaderFreeTrialBannerComponent: FC<Props> = ({ date }) => {
  const tHelper = useTHelper('header.freeTrialBanner');
  const tHelperDuration = useTHelper('common.duration');

  const { sidebarOpen } = useSelector(commonSelector);

  const { Name } = useSelector(deCommonCurrentPartnerSelector);

  const calcDateLeft = useCallback(
    () => ({
      days: differenceInDays(date, CDate.getDateWithoutTime()),
      hours: differenceInHours(date, CDate.getDateWithoutTime()),
    }),
    [date]
  );

  const [dateLeft, setDateLeft] = useState(calcDateLeft);

  useEffect(() => {
    setInterval(() => {
      setDateLeft(calcDateLeft());
    }, 60 * 60 * 1000);
  }, [calcDateLeft]);

  const showModal = useCallback(() => showPlusSubscriptionModal('upgradeNow', { canClose: true }), []);

  const text = useMemo(() => {
    let duration = '';

    if (dateLeft.days > 0) {
      duration = `${dateLeft.days} ${tHelperDuration(dateLeft.days > 1 ? 'days' : 'day')}`;
    } else if (dateLeft.hours > 0) {
      duration = `${dateLeft.hours} ${tHelperDuration(dateLeft.hours > 1 ? 'hours' : 'hour')}`;
    }

    return duration ? tHelper('text', { duration }) : tHelper('textExpired', { partnerName: Name });
  }, [Name, dateLeft.days, dateLeft.hours, tHelper, tHelperDuration]);

  eventPublishShowPlusSubscriptionTrialBanner(null);

  return (
    <div
      className={classNames('free-trial-banner header-banner', {
        sidebarOpen,
      })}
    >
      <div className="free-trial-banner-text">{text}</div>
      <UiButton onClick={showModal}>{tHelper('upgradeBtnText')}</UiButton>
      <UiHoverTooltip text={tHelper('hoverTooltip')} className="free-trial-banner-tooltip" place="bottom">
        <InfoIcon className="tooltip-info-icon" />
      </UiHoverTooltip>
    </div>
  );
};

export const HeaderFreeTrialBanner: FC<HeaderBannerOnShowType> = memo(
  ({ onShow }) => {
    const [date, setDate] = useState<Date | null>(null);

    const { response } = useSelector(currentUserSelector);

    useEffect(() => {
      const organizationUrl = response?.organizationInfo.OrganizationUrl;

      if (organizationUrl) {
        apiGetTrialStatus(organizationUrl).then((response) => {
          if (response.IsActive) {
            flushSync(() => {
              setDate(CDate.parse(response.EndDate, FULL_DATE_FORMAT));
            });

            onShow();
          }
        });
      }
    }, [onShow, response?.organizationInfo.OrganizationUrl]);

    return date ? <HeaderFreeTrialBannerComponent date={date} /> : null;
  },
  () => true
);
